import React, { useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Plus, Edit, Trash2, DollarSign, LayoutGrid, List } from 'lucide-react';
import { toast } from 'sonner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const CategoryBadge = ({ category }) => {
  const categoryColors = {
    'Grooming': 'bg-purple-100 text-purple-800',
    'Medical': 'bg-red-100 text-red-800',
    'Preventive': 'bg-green-100 text-green-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${categoryColors[category] || 'bg-gray-100 text-gray-800'}`}>
      {category}
    </span>
  );
};

export const Services = () => {
  const [services, setServices] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingServiceId, setDeletingServiceId] = useState(null);
  const [viewMode, setViewMode] = useState('card');
  const [formData, setFormData] = useState({
    name: '',
    category: 'Medical',
    price: '',
  });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to load services');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : 0
      };

      if (editingService) {
        await api.put(`/services/${editingService.id}`, payload);
        toast.success('Service updated successfully');
      } else {
        await api.post('/services', payload);
        toast.success('Service created successfully');
      }
      setIsDialogOpen(false);
      setFormData({ name: '', category: 'Medical', price: '' });
      setEditingService(null);
      fetchServices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({
      name: service.name,
      category: service.category,
      price: service.price.toString(),
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (id) => {
    setDeletingServiceId(id);
    setIsDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/services/${deletingServiceId}`);
      toast.success('Service deleted successfully');
      fetchServices();
    } catch (error) {
      toast.error('Failed to delete service');
    } finally {
      setIsDeleteDialogOpen(false);
      setDeletingServiceId(null);
    }
  };

  return (
    <div className="p-6 md:p-12" data-testid="services-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-sage-800 mb-2">Services</h1>
          <p className="text-gray-600">Manage clinic services and pricing</p>
        </div>
        <div className="flex gap-3">
          <div className="flex bg-white rounded-full shadow border border-stone-200">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('card')}
              className={`rounded-full ${viewMode === 'card' ? 'bg-sage-500 text-white hover:bg-sage-600 hover:text-white' : ''}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode('table')}
              className={`rounded-full ${viewMode === 'table' ? 'bg-sage-500 text-white hover:bg-sage-600 hover:text-white' : ''}`}
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-sage-500 hover:bg-sage-600 text-white rounded-full shadow-lg"
              onClick={() => {
                setEditingService(null);
                setFormData({ name: '', category: 'Medical', price: '' });
              }}
              data-testid="add-service-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Service
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl">
            <DialogHeader>
              <DialogTitle>{editingService ? 'Edit Service' : 'Add New Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="service-form">
              <div>
                <Label htmlFor="name">Service Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="rounded-xl mt-2"
                  placeholder="e.g., Bath, Nail Trim, Vaccination"
                  required
                  data-testid="service-name-input"
                />
              </div>
              <div>
                <Label htmlFor="category">Category</Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                  <SelectTrigger className="rounded-xl mt-2" data-testid="service-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Grooming">Grooming</SelectItem>
                    <SelectItem value="Medical">Medical</SelectItem>
                    <SelectItem value="Preventive">Preventive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="price">Price</Label>
                <Input
                  id="price"
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="rounded-xl mt-2"
                  placeholder="0.00"
                  data-testid="service-price-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white" data-testid="service-submit-button">
                {editingService ? 'Update Service' : 'Create Service'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {services.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-3xl shadow-lg">
          No services yet. Add your first service to get started!
        </div>
      ) : viewMode === 'card' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, idx) => (
            <div
              key={service.id}
              className="bg-white rounded-3xl shadow-lg p-6 hover:shadow-2xl transition-all duration-300"
              data-testid={`service-card-${idx}`}
            >
              <div className="mb-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-lg font-heading font-bold text-sage-800">{service.name}</h3>
                  <CategoryBadge category={service.category} />
                </div>
                <div className="flex items-center gap-2 text-xl font-bold text-sage-600 mb-4">
                  {service.price.toFixed(2)}
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleEdit(service)}
                  className="rounded-full flex-1"
                  data-testid={`edit-service-${idx}`}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(service.id)}
                  className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 flex-1"
                  data-testid={`delete-service-${idx}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-3xl shadow-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-bold text-sage-800">Service Name</TableHead>
                <TableHead className="font-bold text-sage-800">Category</TableHead>
                <TableHead className="font-bold text-sage-800">Price</TableHead>
                <TableHead className="font-bold text-sage-800 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services.map((service, idx) => (
                <TableRow key={service.id} data-testid={`service-row-${idx}`}>
                  <TableCell className="font-medium">{service.name}</TableCell>
                  <TableCell>
                    <CategoryBadge category={service.category} />
                  </TableCell>
                  <TableCell className="text-sage-600 font-semibold">${service.price.toFixed(2)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(service)}
                        className="rounded-full"
                        data-testid={`edit-service-${idx}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(service.id)}
                        className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-service-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the service.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700 text-white rounded-xl">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
